import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { financeiroService } from '@/services/financeiroService';
import { configuracoesFinanceiroService } from '@/services/configuracoesFinanceiroService';
import { formatarCnpj, useEmpresaFinanceira } from '@/contexts/EmpresaFinanceiraContext';

const FORMAS = ['PIX', 'TED', 'Boleto', 'Débito em Conta', 'Transferência Bancária', 'Outros'];

export function LancamentoManualBanco() {
  const { empresa, empresaId, contaBancaria } = useEmpresaFinanceira();
  const [tipo, setTipo] = useState<'saida' | 'entrada'>('saida');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [data, setData] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [contraparte, setContraparte] = useState('');
  const [categoria, setCategoria] = useState('');
  const [forma, setForma] = useState('PIX');
  const [categorias, setCategorias] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    configuracoesFinanceiroService.getConfig().then((config) => {
      const lista = tipo === 'saida' ? config?.contasPagar?.categorias : config?.contasReceber?.categorias;
      setCategorias(lista || []);
    }).catch(() => setCategorias([]));
  }, [tipo]);

  const limpar = () => {
    setDescricao('');
    setValor('');
    setContraparte('');
    setCategoria('');
  };

  const salvar = async () => {
    if (!empresaId || !empresa) {
      toast.error('Selecione a Sun7 Energy para lançar o extrato do C6 Bank.');
      return;
    }
    const textoValor = valor.trim();
    const valorNumero = textoValor.includes(',')
      ? Number(textoValor.replace(/\./g, '').replace(',', '.'))
      : Number(textoValor);
    if (!descricao.trim() || !categoria || !data || !Number.isFinite(valorNumero) || valorNumero <= 0) {
      toast.error('Informe descrição, categoria, data e um valor maior que zero.');
      return;
    }

    const banco = contaBancaria?.apelido || 'C6 Bank';
    setSalvando(true);
    try {
      if (tipo === 'saida') {
        await financeiroService.criarContaPagar({
          descricao: descricao.trim(),
          valor: valorNumero,
          data_vencimento: data,
          data_pagamento: data,
          status: 'pago',
          categoria,
          fornecedor: contraparte.trim() || undefined,
          tipo_pagamento: 'avista',
          forma_pagamento: forma,
          observacoes: `Lançamento manual via ${banco}.`,
          empresa_id: empresaId,
        });
        toast.success('Saída lançada em contas a pagar.');
      } else {
        await financeiroService.criarContaReceber({
          descricao: descricao.trim(),
          valor: valorNumero,
          data_vencimento: data,
          data_recebimento: data,
          status: 'recebido',
          categoria,
          cliente: contraparte.trim() || undefined,
          tipo_pagamento: 'avista',
          forma_pagamento: forma,
          observacoes: `Lançamento manual via ${banco}.`,
          empresa_id: empresaId,
        });
        toast.success('Entrada lançada em contas a receber.');
      }
      limpar();
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível lançar a movimentação.');
    } finally {
      setSalvando(false);
    }
  };

  if (!empresa) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{contaBancaria?.apelido || 'C6 Bank'}</CardTitle>
        <CardDescription>
          {empresa.nome_fantasia} — CNPJ {formatarCnpj(empresa.cnpj)}. O C6 Bank não tem API: cada movimentação é lançada aqui e entra na empresa selecionada.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Tipo</Label>
          <Select value={tipo} onValueChange={(valorSelecionado: 'saida' | 'entrada') => setTipo(valorSelecionado)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="saida">Saída (conta a pagar)</SelectItem>
              <SelectItem value="entrada">Entrada (conta a receber)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data</Label>
          <Input type="date" value={data} onChange={(event) => setData(event.target.value)} />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Descrição</Label>
          <Input value={descricao} onChange={(event) => setDescricao(event.target.value)} placeholder="Ex.: tarifa, fornecedor, recebimento" />
        </div>
        <div className="space-y-2">
          <Label>Valor</Label>
          <Input value={valor} onChange={(event) => setValor(event.target.value)} placeholder="0,00" inputMode="decimal" />
        </div>
        <div className="space-y-2">
          <Label>Forma</Label>
          <Select value={forma} onValueChange={setForma}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FORMAS.map((item) => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{tipo === 'saida' ? 'Fornecedor' : 'Cliente'}</Label>
          <Input value={contraparte} onChange={(event) => setContraparte(event.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Categoria</Label>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {categorias.map((item) => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button onClick={salvar} disabled={salvando}>
            {salvando ? 'Lançando...' : 'Lançar na empresa'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
